"use client";

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, useSyncExternalStore, type PointerEvent as ReactPointerEvent, type SyntheticEvent } from "react";
import FullscreenExitIcon from "@/assets/icons/video-fullscreen-exit.svg";
import FullscreenIcon from "@/assets/icons/video-fullscreen.svg";
import PauseIcon from "@/assets/icons/video-pause.svg";
import PlayIcon from "@/assets/icons/video-play.svg";
import VolumeOffIcon from "@/assets/icons/video-volume-off.svg";
import VolumeOnIcon from "@/assets/icons/video-volume-on.svg";
import { cn } from "@/lib/utils";
import classes from "./video-player.module.scss";
export type VideoPlayerHandle = {
  getCurrentTime: () => number;
  seekTo: (time: number) => void;
  play: () => Promise<void>;
  pause: () => void;
};
export type VideoPlayerProps = {
  /** Video source URL (mp4 / webm / other HTML5-supported media) */
  src: string;
  /** Poster image shown before the first frame / while loading */
  poster?: string;
  /** Accessible label for the media */
  "aria-label"?: string;
  /** Loop playback — default false */
  loop?: boolean;
  /** Mute audio — default false */
  muted?: boolean;
  /** Start / resume playback when true — default false */
  autoPlay?: boolean;
  /** Show custom glass controls (native browser controls are never used) */
  controls?: boolean;
  /** Fired when playback reaches the end (ignored when `loop` is true) */
  onEnded?: () => void;
  className?: string;
};
const emptySubscribe = () => () => undefined;

/* Safari (older iPadOS) only has prefixed element fullscreen; iPhone only supports native video fullscreen. */
type WebkitFullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};
type WebkitFullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};
type WebkitFullscreenVideo = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
};
const getFullscreenElement = () => {
  const doc = document as WebkitFullscreenDocument;
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
};

/**
 * Dumb native HTML5 video player with optional Figma glass controls.
 * Feature-specific playback policy belongs in consumers.
 */
export const VideoPlayer = forwardRef<VideoPlayerHandle, VideoPlayerProps>(function VideoPlayer({
  src,
  poster,
  loop = false,
  muted = false,
  autoPlay = false,
  controls = false,
  onEnded,
  className,
  "aria-label": ariaLabel = "Video"
}, ref) {
  const rootRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<HTMLVideoElement | null>(null);
  const hideTimerRef = useRef<number | null>(null);
  const mounted = useSyncExternalStore(emptySubscribe, () => true, () => false);
  const [playing, setPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [controlsVisible, setControlsVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  /* Reset local UI state when the source / mute prop changes (render-time, not an effect).
   * The user's mute choice is kept across source changes (e.g. auto-advance to the next clip). */
  const [prevSrc, setPrevSrc] = useState(src);
  const [prevMutedProp, setPrevMutedProp] = useState(muted);
  if (prevSrc !== src) {
    setPrevSrc(src);
    setProgress(0);
    setDuration(0);
  }
  if (prevMutedProp !== muted) {
    setPrevMutedProp(muted);
    setIsMuted(muted);
  }
  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current == null) return;
    window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = null;
  }, []);
  const revealControls = useCallback(() => {
    if (!controls) return;
    setControlsVisible(true);
    clearHideTimer();
    hideTimerRef.current = window.setTimeout(() => {
      setControlsVisible(false);
    }, 2200);
  }, [clearHideTimer, controls]);
  const playVideo = useCallback(async () => {
    try {
      await playerRef.current?.play();
    } catch {
      /* Autoplay / play may be blocked */
    }
  }, []);
  const pauseVideo = useCallback(() => {
    playerRef.current?.pause();
  }, []);
  useImperativeHandle(ref, () => ({
    getCurrentTime: () => playerRef.current?.currentTime ?? 0,
    seekTo: (time: number) => {
      const media = playerRef.current;
      if (!media || !Number.isFinite(time)) return;
      try {
        media.currentTime = Math.max(0, time);
      } catch {
        /* Cross-origin seek may be blocked */
      }
    },
    play: playVideo,
    pause: pauseVideo
  }));

  /* Parent-driven play/pause (e.g. delayed autoplay). UI state follows media events. */
  useEffect(() => {
    const media_0 = playerRef.current;
    if (!media_0 || !mounted) return;
    if (autoPlay) {
      void media_0.play().catch(() => undefined);
      return;
    }
    media_0.pause();
  }, [autoPlay, src, mounted]);
  useEffect(() => () => clearHideTimer(), [clearHideTimer]);
  useEffect(() => {
    const syncFullscreen = () => {
      const element = getFullscreenElement();
      setIsFullscreen(element != null && element === rootRef.current);
    };
    document.addEventListener("fullscreenchange", syncFullscreen);
    document.addEventListener("webkitfullscreenchange", syncFullscreen);
    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreen);
      document.removeEventListener("webkitfullscreenchange", syncFullscreen);
    };
  }, []);
  const seekToRatio = useCallback((ratio: number) => {
    const media_1 = playerRef.current;
    if (!media_1 || !Number.isFinite(media_1.duration) || media_1.duration <= 0) return;
    const next = Math.min(1, Math.max(0, ratio)) * media_1.duration;
    try {
      media_1.currentTime = next;
      setProgress(next);
    } catch {
      /* Cross-origin seek may be blocked */
    }
  }, []);
  const handleTimelinePointer = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;
    seekToRatio((event.clientX - rect.left) / rect.width);
    revealControls();
  }, [revealControls, seekToRatio]);
  const toggleFullscreen = useCallback(async () => {
    const root = rootRef.current as WebkitFullscreenElement | null;
    const video = playerRef.current as WebkitFullscreenVideo | null;
    if (!root) return;
    const doc = document as WebkitFullscreenDocument;
    const activeFullscreenElement = getFullscreenElement();
    try {
      if (activeFullscreenElement) {
        if (doc.exitFullscreen) {
          await doc.exitFullscreen();
        } else if (doc.webkitExitFullscreen) {
          await doc.webkitExitFullscreen();
        }
      } else if (root.requestFullscreen) {
        await root.requestFullscreen();
      } else if (root.webkitRequestFullscreen) {
        await root.webkitRequestFullscreen();
      } else if (video?.webkitEnterFullscreen) {
        /* Safari iOS video element fallback */
        video.webkitEnterFullscreen();
      }
    } catch {
      /* Fullscreen action failed or was blocked by browser policy */
    } finally {
      revealControls();
    }
  }, [revealControls]);
  const togglePlayPause = useCallback(() => {
    if (playing) pauseVideo();else void playVideo();
    revealControls();
  }, [pauseVideo, playVideo, playing, revealControls]);
  const progressRatio = duration > 0 ? Math.min(1, Math.max(0, progress / duration)) : 0;
  let mediaContent = null;
  if (mounted) {
    mediaContent = <video ref={playerRef} className={classes.video} src={src} poster={poster} muted={isMuted} loop={loop} playsInline preload="metadata" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onDurationChange={(event_0: SyntheticEvent<HTMLVideoElement>) => {
      const next_0 = event_0.currentTarget.duration;
      if (Number.isFinite(next_0)) setDuration(next_0);
    }} onTimeUpdate={(event_1: SyntheticEvent<HTMLVideoElement>) => {
      setProgress(event_1.currentTarget.currentTime);
    }} onVolumeChange={(event_2: SyntheticEvent<HTMLVideoElement>) => {
      setIsMuted(event_2.currentTarget.muted);
    }} onEnded={() => {
      setPlaying(false);
      if (!loop) onEnded?.();
    }} />;
  } else if (poster) {
    // eslint-disable-next-line @next/next/no-img-element -- SSR/hydration placeholder
    mediaContent = <img src={poster} alt="" className={classes.poster} />;
  }
  return <div ref={rootRef} data-slot="video-player" data-controls={controls ? "true" : undefined} data-controls-visible={controls && (controlsVisible || !playing) ? "true" : undefined} className={cn(classes.root, controls && classes.rootControls, className)} aria-label={ariaLabel} onMouseEnter={revealControls} onMouseMove={revealControls} onMouseLeave={() => {
    if (!controls) return;
    clearHideTimer();
    setControlsVisible(false);
  }}>
        {mediaContent}

        {controls ? <>
            <button type="button" className={classes.centerHit} aria-label={playing ? "Pause" : "Play"} onClick={togglePlayPause} />

            <div className={classes.controls} data-slot="video-player-controls">
              <div className={classes.controlsRow}>
                <div className={classes.leftButtons}>
                  <button type="button" className={classes.glassIconButton} aria-label={playing ? "Pause" : "Play"} onClick={togglePlayPause}>
                    {playing ? <PauseIcon aria-hidden /> : <PlayIcon aria-hidden />}
                  </button>
                  <button type="button" className={classes.glassIconButton} aria-label={isMuted ? "Unmute" : "Mute"} onClick={() => {
              setIsMuted(prev => !prev);
              revealControls();
            }}>
                    {isMuted ? <VolumeOffIcon aria-hidden /> : <VolumeOnIcon aria-hidden />}
                  </button>
                </div>

                <div className={classes.timeline} role="slider" aria-label="Seek" aria-valuemin={0} aria-valuemax={Math.round(duration) || 0} aria-valuenow={Math.round(progress)} onPointerDown={handleTimelinePointer}>
                  <span className={classes.timelineFill} style={{
              width: `${progressRatio * 100}%`
            }} />
                  <span className={classes.timelineHandle} style={{
              left: `${progressRatio * 100}%`
            }} />
                  <input className={classes.timelineInput} type="range" min={0} max={duration || 1} step={0.05} value={progress} aria-label="Seek position" onChange={event_3 => {
              seekToRatio(Number(event_3.target.value) / (duration || 1));
              revealControls();
            }} />
                </div>

                <div className={classes.rightButtons}>
                  <button type="button" className={classes.glassIconButton} aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"} onClick={() => {
              void toggleFullscreen();
            }}>
                    {isFullscreen ? <FullscreenExitIcon aria-hidden /> : <FullscreenIcon aria-hidden />}
                  </button>
                </div>
              </div>
            </div>
          </> : null}
      </div>;
});